import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChange } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { Cost } from "../../../../../../models/cost";
import { CostType } from "../../../../../../models/cost-type";
import { FixCost } from "../../../../../../models/fix-cost";
import { FormulaCost } from "../../../../../../models/formula-cost";
import { CostControllerService } from "../../../../../../services/cost-controller/cost-controller.service";
import { CurrencyControllerService } from "../../../../../../services/currency-controller/currency-controller.service";
import { CostView } from "../../../../../../models/infix-formula/costView";
import { Registration } from "../../../../../../models/registration";
import { CostService } from "../../../../../../services/cost-controller/cost.service";
import { Store } from "../../../../../../services/store.service";
import {TranslatedBaseError} from "../../../../TranslatedBaseError";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: "cost-edit",
  templateUrl: "./cost-edit.component.html",
  styles: [],
  providers: [ToastrService],
})
export class CostEditComponent extends TranslatedBaseError implements OnInit, OnChanges, OnDestroy {

  @Input() public serviceId: string;
  @Input() public cost: Cost;
  @Input() public registration: Registration;
  @Input() public costView: CostView;
  @Output() public formSubmit = new EventEmitter();
  @Output() public editCancel = new EventEmitter();
  @Output() public registrationCostFormSubmit = new EventEmitter();
  @Input() public addNew: boolean = false;
  @Input() public determinantMapById: { [key: string]: string };

  public showDeterminantBuilder: boolean = false;

  public currencies: any[] = [];
  public fixValue: number;
  public formulaCost: FormulaCost = new FormulaCost();
  public editOrNew: string;
  public additionalInformation: string;
  public determinants: any[] = [];
  public determinantValues: any[] = [];
  public selectedDeterminantValues: any[] = [];
  public registrationCostList: any = [];
  public disabled = false;
  public tabToShow = "DETAILS";
  public costTypeChanged: boolean = false;

  public openFormula: boolean = false;
  public openDeterminantFormula: boolean = false;

  public subscription: any;
  public isLoading: boolean = false;

  constructor(private costControllerService: CostControllerService,
              private currencyControllerService: CurrencyControllerService,
              private costService: CostService,
              public toastr: ToastrService,
              public store: Store,
              public translateService: TranslateService) {
    super(toastr, translateService);
  }

  public ngOnInit() {
    this.subscription = this.costService.editData.subscribe((data) => {
      console.log("CostEditComponent: editData subscription", data);
      this.serviceId = data.serviceId;
      this.registration = data.registration;
      this.cost = data.cost;
      this.costView = data.costView;
      this.addNew = data.addNew;
      this.showDeterminantBuilder = data.showDeterminantBuilder;

      this.initCost();
    });
    this.getCurrencies();
  }

  public changeFormula(data: any) {
    console.log("CostEditComponent: changeFormula", data);
    this.openFormula = true;
    this.showDeterminantBuilder = true;
    this.costView = data.costView;
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    if (changes.serviceId && changes.serviceId.currentValue) {
      this.serviceId = changes.serviceId.currentValue;
    }
    if (changes.cost) {
      if (changes.cost.currentValue) {
        this.cost = changes.cost.currentValue;
      } else {
        this.cost = new Cost();
      }
      this.initCost();
    }
  }

  public initCost() {
    if (this.cost != null && this.cost.costType != null) {
      if (this.cost.costType === CostType.FIX) {
        const c = this.cost as FixCost;
        this.fixValue = c.fixValue;
      } else if (this.cost.costType === CostType.FORMULA) {
        const c = Object.assign(new FormulaCost(), this.cost);
        this.formulaCost = c;
      }
      this.costTypeChanged = false;
    }
  }

  public save() {
    const costType: CostType = this.cost.costType;
    let cost: Cost;
    if (costType === CostType.FIX) {
      cost = new FixCost();
      (cost as FixCost).fixValue = this.fixValue;
    } else if (costType === CostType.FORMULA) {
      cost = new FormulaCost();
      (cost as FormulaCost).formulaCostItems = (this.cost as FormulaCost).formulaCostItems;
    } else {
      // TODO: Throw error…
    }
    cost.id = this.cost.id;
    cost.name = this.cost.name;
    cost.currencyId = this.cost.currencyId;
    cost.costType = costType;
    cost.jsonDeterminants = this.cost.jsonDeterminants;

    if (this.cost.id === undefined || this.costTypeChanged) {
      this.createCost(cost);
    } else {
      this.saveCost(cost);
    }
  }

  public createCost(cost: Cost) {
    if (!this.isLoading) {
      this.isLoading = true;
      const dataCb = (data: Cost) => {
        if (data.costType === CostType.FORMULA) {
          data = Object.assign(new FormulaCost(), data);
        }
        this.formSubmit.emit({ cost: data, create: true });
        this.isLoading = false;
      };
      const errorCb = (error: any) => { this.showErrorMessage(error); this.isLoading = false; };
      if (cost.costType === CostType.FIX) {
        this.costControllerService.createFixCost(this.registration.id, cost as FixCost).subscribe(dataCb, errorCb);
      } else if (cost.costType === CostType.FORMULA) {
        this.costControllerService.createFormulaCost(this.registration.id, cost as FormulaCost).subscribe(dataCb, errorCb);
      }
    }
  }

  public saveCost(cost: Cost) {
    if (!this.isLoading) {
      this.isLoading = true;
      const dataCb = () => {
        this.responseSuccessful("Cost saved!");
        this.formSubmit.emit({ save: true });
        this.isLoading = false;
      };
      const errorCb = (error: any) => this.showErrorMessage(error);
      if (cost.costType === CostType.FIX) {
        this.costControllerService.saveFixCost(cost as FixCost).subscribe(dataCb, errorCb);
      } else if (cost.costType === CostType.FORMULA) {
        this.costControllerService.saveFormulaCost(cost as FormulaCost).subscribe(dataCb, errorCb);
      }
    }
  }

  public getCurrencies() {
    this.currencyControllerService.getEnabledCurrencies().subscribe(
      (data) => {
        this.currencies = data;
      },
      (error) => this.showErrorMessage(error),
    );
  }

  public isFixCost(): boolean {
    return this.cost.costType === CostType.FIX;
  }

  public isFormulaCost(): boolean {
    return this.cost.costType === CostType.FORMULA;
  }

  public resetForm() {
    this.cost = null;
  }

  public closeLayer() {
    this.editCancel.emit({ cancel: true });
    this.resetForm();
  }

  public closeDetLayer() {
    this.store.state.common.determinantSection.reset();
  }

  public costTypeChangedEvent(costType: CostType) {
    this.costTypeChanged = true;
  }

  appendDeterminant = (det) => {
    let data = JSON.parse(this.cost.jsonDeterminants);
    if (!Array.isArray(data)) {
      data = [];
    }
    if (data.length === 0) {
      data.push({
        type: 'OR',
        items: []
      });
    }
    let lastItem = data[data.length - 1];
    if (lastItem.items == null) {
      lastItem = {
        type: 'OR',
        items: []
      };
      data.push(lastItem);
    }
    lastItem.items.push({
      type: det.type || 'OR',
      determinantId: det.determinantId,
    });
    this.cost.jsonDeterminants = JSON.stringify(data);
  };
}
